import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next/src';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import Dialog from '@hobs/components/Dialog';
import Input from '@hobs/components/Input';
import InputLabel from '@hobs/components/InputLabel';
import {
  uploadAttachments,
  clearAttachments,
  deleteAttachment
} from '@hobs/actions/attachment';

import DropZone from '@hobs/components/DropZone';
import Toast from '@hobs/components/Toast';
import { Formik, Form, Field } from 'formik';
import { RaisedButton } from '@hobs/components/Buttons';
import Alert from '@hobs/components/Alert';
import Switch from '@hobs/components/Switch';
import {
  createInvoice,
  getForSubmit,
  clearInvoices
} from '@hobs/actions/invoices';
import Money from '@hobs/components/Money';

import './InvoiceCreateDialog.scss';

const defaultState = {
  error: null,
  isPaid: false
};

class InvoiceCreateDialog extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = defaultState;

    const { t } = props;
    this.validationSchema = Yup.object().shape({
      receiver: Yup.string()
        .required(t('Receiver is required'))
        .max(256, t('Receiver cannot be longer than 256 symbols'))
        .nullable()
    });
  }

  componentDidUpdate(prevProps) {
    const { getForSubmit, serviceRequestIds } = this.props;
    if (
      serviceRequestIds &&
      serviceRequestIds !== prevProps.serviceRequestIds
    ) {
      getForSubmit({ serviceRequestIds }).then(response => {
        if (!response.success && response.payload) {
          Toast.warning(response.payload.message);
        }
      });
    }
  }

  onDialogClose = () => {
    const { closeDialog, clearAttachments } = this.props;

    clearAttachments();
    closeDialog();
  };

  onDialogClosed = () => {
    const { clearInvoices } = this.props;
    clearInvoices();
    this.resetState();
  };

  setError(message) {
    this.setState({ error: message });
  }

  setIsPaid = isPaid => {
    this.setState({ isPaid });
  };

  resetState() {
    this.setState(defaultState);
  }

  handleFileUpload = value => {
    if (value) {
      this.props.uploadAttachments({ attachments: value });
    }
  };

  submitInvoice = (values, { setSubmitting, setErrors }) => {
    const {
      createInvoice,
      t,
      reload,
      serviceRequestIds,
      attachments
    } = this.props;
    const { isPaid } = this.state;

    const body = {
      ...values,
      serviceRequestIds,
      isPaid,
      attachmentsIds: attachments.map(attachment => attachment.attachmentId)
    };

    createInvoice(body).then(response => {
      if (response.success) {
        Toast.success(t('Invoice reference created successfully'));
        reload();
        this.onDialogClose();
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [error.target]: error.message[0]
          };
        }, {});
        if (errors) {
          setErrors(errors);
        }
      } else if (response.payload) {
        this.setError(response.payload.message);
      } else {
        setErrors(t('Cannot create invoice reference'));
      }

      setSubmitting(false);
    });
  };

  renderExerciseCurrency() {
    const { currentExercise } = this.props;
    
    if (!currentExercise.currency.name) {
      return;
    }

    return ` (${this.props.currentExercise.currency.name})`
  }

  render() {
    const { t, isDialogOpen, forSubmit, attachments } = this.props;
    const { error, isPaid } = this.state;

    return (
      <Dialog
        title={t('Create invoice reference')}
        isOpen={isDialogOpen && forSubmit}
        onClose={this.onDialogClose}
        onClosed={this.onDialogClosed}
        className="invoice"
      >
        <Formik
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={this.validationSchema}
          enableReinitialize={true}
          initialValues={{
            ...forSubmit,
            receiver: null,
            attachmentsIds: []
          }}
          onSubmit={this.submitInvoice}
        >
          {({ setFieldValue, values, isSubmitting }) => (
            <Form>
              {error && <Alert type="error">{error}</Alert>}
              <div>
                <div className="invoice--info invoice--description">
                  {t(
                    'Are you sure you want to create invoice reference for the selected service lines?'
                  )}
                </div>
                <div className="invoice--info invoice--description">
                  {t('The following information will be included:')}
                </div>
                <div className="invoice--row">
                  <Field
                    setFieldValue={setFieldValue}
                    name="receiver"
                    placeholder="Enter the receiver"
                    component={Input}
                  >
                    {t('Receiver')}
                  </Field>
                </div>
                <div className="invoice--row">
                  <div className="invoice--column">
                    <div className="invoice--row">
                      <div className="invoice--column">
                        <InputLabel>{t('Invoiced service lines')}</InputLabel>
                        <div className="invoice--info">
                          {values.serviceRequestCount}
                        </div>
                      </div>
                    </div>
                    <div className="invoice--row">
                      {this.props.withIsPaidSwitch && (
                        <div className="invoice--column">
                          <InputLabel>{t('Is paid')}</InputLabel>
                          <Switch checked={isPaid} onChange={this.setIsPaid} />
                        </div>
                      )}
                    </div>
                    <div className="invoice--row">
                      <div className="invoice--column">
                        <InputLabel>
                          {t('Total actual cost')} 
                          {this.renderExerciseCurrency()}
                        </InputLabel>
                        <div className="invoice--info">
                          <Money amount={values.totalCost} />
                        </div>
                      </div>
                    </div>
                    <div className="invoice--row">
                      <div className="invoice--column">
                        <InputLabel>
                          {t('Total actual gross amount')}
                          {this.renderExerciseCurrency()}
                        </InputLabel>
                        <div className="invoice--info">
                          <Money amount={values.totalGrossAmount} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="invoice--column">
                    <InputLabel>{t('Attachments')}</InputLabel>
                    <DropZone
                      multiple={true}
                      value={attachments}
                      onSelect={files => {
                        this.handleFileUpload(files);
                      }}
                      onRemove={file => {
                        this.props.deleteAttachment(file);
                      }}
                    />
                  </div>
                </div>
                <div className="invoice--buttons">
                  <RaisedButton type="submit" disabled={isSubmitting}>
                    {t('Create')}
                  </RaisedButton>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    );
  }
}

InvoiceCreateDialog.defaultProps = {
  withIsPaidSwitch: true
};

InvoiceCreateDialog.propTypes = {
  t: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  isDialogOpen: PropTypes.bool,
  closeDialog: PropTypes.func,
  serviceRequestIds: PropTypes.array,
  createInvoice: PropTypes.func,
  forSubmit: PropTypes.object,
  getForSubmit: PropTypes.func,
  reload: PropTypes.func,
  clearInvoices: PropTypes.func,
  attachments: PropTypes.array,
  uploadAttachments: PropTypes.func,
  setAttachments: PropTypes.func,
  deleteAttachment: PropTypes.func,
  clearAttachments: PropTypes.func,
  withIsPaidSwitch: PropTypes.bool,
  currentExercise: PropTypes.object
};

const mapStateToProps = state => ({
  forSubmit: state.invoices.forSubmit,
  attachments: state.attachment.attachments,
  currentExercise: state.exercise.current
});

const mapDispatchToProps = {
  createInvoice,
  getForSubmit,
  clearInvoices,
  uploadAttachments,
  clearAttachments,
  deleteAttachment
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(InvoiceCreateDialog))
);
